import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef } from "react";
import { combineClasses } from "../../../utilities/combineClasses.js";
import BaseTextInput_module from "./BaseTextInput.module.js";
import { useInputAccessibilityProps } from "../../form/internal/useInputAccessibilityProps.js";
import { createComponentNamespace } from "../../../utilities/createComponentNamespace.js";
import { a11yIgnore } from "../../../utilities/a11yIgnore.js";
import { AdornmentButton, AdornmentButtonSeparator, AdornmentIcon, AdornmentText } from "./BaseTextInputAdornments.js";
const Wrapper = /*#__PURE__*/ forwardRef(({ className, status, size = 'medium', disabled, readOnly, testId, ...props }, ref)=>/*#__PURE__*/ jsx("span", {
        className: combineClasses(BaseTextInput_module.wrapper, disabled && BaseTextInput_module.disabled, readOnly && BaseTextInput_module.readOnly, className),
        "data-status": status,
        "data-size": size,
        "data-testid": testId,
        ref: ref,
        ...props
    }));
const DISABLED_AUTOFILL_VALUE = 'off-do-not-fill';
const Control = /*#__PURE__*/ forwardRef(({ testId = 'input', onChange, onValueChange, as, className, autoComplete = DISABLED_AUTOFILL_VALUE, 'aria-describedby': ariaDescribedBy, ...props }, ref)=>{
    const inputProps = useInputAccessibilityProps({
        ariaDescribedBy
    });
    const As = as ?? 'input';
    return /*#__PURE__*/ jsx(As, {
        size: 1,
        ...inputProps,
        className: combineClasses(className, BaseTextInput_module.control),
        "data-testid": testId,
        onChange: (event)=>{
            onValueChange?.(event.target.value);
            onChange?.(event);
        },
        ...a11yIgnore('autocomplete-valid'),
        autoComplete: autoComplete,
        ...props,
        ref: ref
    });
});
const BaseTextInput = /*#__PURE__*/ forwardRef(({ status, size, wrapperProps, startAdornment, endAdornment, ...props }, ref)=>/*#__PURE__*/ jsxs(Wrapper, {
        status: status,
        disabled: props.disabled,
        readOnly: props.readOnly,
        size: size,
        ...wrapperProps,
        children: [
            startAdornment,
            /*#__PURE__*/ jsx(Control, {
                ...props,
                ref: ref
            }),
            endAdornment
        ]
    }));
const FakeControl = /*#__PURE__*/ forwardRef(({ className, value, placeholder, disabled, readOnly, required, testId, ...props }, ref)=>/*#__PURE__*/ jsx("span", {
        ref: ref,
        className: combineClasses(className, BaseTextInput_module.fakeControl, !value && placeholder && BaseTextInput_module.placeholder, disabled && BaseTextInput_module.disabled, readOnly && BaseTextInput_module.readOnly),
        "aria-disabled": disabled,
        "aria-required": required,
        "aria-readonly": readOnly,
        "data-testid": testId,
        ...a11yIgnore('color-contrast'),
        ...props,
        children: value || placeholder
    }));
FakeControl.displayName = 'FakeControl';
const BaseTextInputNamespace = createComponentNamespace({
    component: BaseTextInput,
    componentList: {
        Wrapper,
        Control,
        FakeControl,
        AdornmentButton: AdornmentButton,
        AdornmentButtonSeparator: AdornmentButtonSeparator,
        AdornmentText: AdornmentText,
        AdornmentIcon: AdornmentIcon
    },
    namespace: 'BaseTextInput'
});
BaseTextInputNamespace.displayName = 'BaseTextInput';
export { BaseTextInputNamespace as BaseTextInput, Control };
